import React from 'react';
import {
  Ban,
  BookOpen,
  Building2,
  CalendarDays,
  ChevronRight,
  CircleSlash,
  KeyRound,
  Shield,
  UserRound,
  Users,
} from 'lucide-react';

function cardClass(isLight: boolean): string {
  return `rounded-2xl p-5 ${
    isLight
      ? 'bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] ring-1 ring-black/[0.04]'
      : 'bg-neutral-900 ring-1 ring-white/10'
  }`;
}

function iconWrap(isLight: boolean, tone: 'indigo' | 'sky' | 'violet' | 'neutral' = 'indigo'): string {
  const tones = {
    indigo: isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300',
    sky: isLight ? 'bg-sky-50 text-sky-600' : 'bg-sky-500/15 text-sky-300',
    violet: isLight ? 'bg-violet-50 text-violet-600' : 'bg-violet-500/15 text-violet-300',
    neutral: isLight ? 'bg-neutral-100 text-neutral-500' : 'bg-neutral-800 text-neutral-400',
  };
  return `inline-flex h-9 w-9 items-center justify-center rounded-xl ${tones[tone]}`;
}

function FlowArrow({ isLight }: { isLight: boolean }) {
  return (
    <span className={`hidden sm:inline-flex items-center self-center ${isLight ? 'text-neutral-300' : 'text-neutral-600'}`} aria-hidden>
      <svg width="36" height="12" viewBox="0 0 36 12" fill="none">
        <path d="M0 6h30" stroke="currentColor" strokeWidth="1.5" />
        <path d="M28 1.5L34 6l-6 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function SkipLabel({ isLight }: { isLight: boolean }) {
  return (
    <span className={`hidden sm:flex flex-col items-center self-center px-1 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>
      <svg width="40" height="12" viewBox="0 0 40 12" fill="none" aria-hidden>
        <path d="M0 6h34" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3" />
        <path d="M32 1.5L38 6l-6 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="text-[10px] mt-0.5">skip if inactive</span>
    </span>
  );
}

export default function QuotaGuideTab({ isLight }: { isLight: boolean }) {
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const body = isLight ? 'text-neutral-700' : 'text-neutral-300';
  const chip = isLight ? 'bg-neutral-50 ring-1 ring-black/[0.04]' : 'bg-neutral-950/60 ring-1 ring-white/10';

  const assignSteps = [
    { icon: CalendarDays, tone: 'indigo' as const, label: 'Assign quota', hint: 'Client and month' },
    { icon: Users, tone: 'violet' as const, label: 'Pick a scope', hint: 'Department, role, or user' },
    { icon: UserRound, tone: 'sky' as const, label: 'Each user gets it', hint: '5M is 5M each, not a pool' },
    { icon: KeyRound, tone: 'indigo' as const, label: 'Track spend', hint: 'From API-key usage' },
  ];

  const overrideSteps = [
    { icon: UserRound, tone: 'indigo' as const, label: 'User', hint: 'Wins first' },
    { icon: Shield, tone: 'sky' as const, label: 'Role', hint: 'If no user grant' },
    { icon: Building2, tone: 'violet' as const, label: 'Department', hint: 'If no role grant' },
    { icon: CircleSlash, tone: 'neutral' as const, label: '0', hint: 'Nothing assigned' },
  ];

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-start gap-3">
        <span className={iconWrap(isLight)}>
          <BookOpen size={16} />
        </span>
        <div>
          <p className={`text-sm font-medium ${title}`}>Guide</p>
          <p className={`text-xs mt-0.5 ${muted}`}>How monthly quotas and overrides work</p>
        </div>
      </div>

      <section className={cardClass(isLight)}>
        <h2 className={`text-sm font-medium ${title}`}>How quotas work</h2>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-0">
          {assignSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <React.Fragment key={step.label}>
                {index > 0 ? <FlowArrow isLight={isLight} /> : null}
                <div className={`flex-1 min-w-0 rounded-xl px-3 py-3 ${chip}`}>
                  <span className={iconWrap(isLight, step.tone)}>
                    <Icon size={15} />
                  </span>
                  <p className={`mt-2.5 text-sm font-medium ${title}`}>{step.label}</p>
                  <p className={`text-[11px] mt-0.5 leading-relaxed ${muted}`}>{step.hint}</p>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <ul className={`mt-4 space-y-2 text-sm leading-relaxed ${body}`}>
          <li className="flex gap-2">
            <ChevronRight size={14} className={`mt-1 shrink-0 ${isLight ? 'text-indigo-400' : 'text-indigo-400'}`} />
            Assign a monthly token limit per client from <span className="font-medium">Assign quota</span>.
          </li>
          <li className="flex gap-2">
            <ChevronRight size={14} className="mt-1 shrink-0 text-indigo-400" />
            <span>
              Choose department, role, or user. A department or role limit of 5M means{' '}
              <span className="font-medium">each matching user gets 5M</span>, not a shared pool.
            </span>
          </li>
          <li className="flex gap-2">
            <ChevronRight size={14} className="mt-1 shrink-0 text-indigo-400" />
            Spend comes from that user’s API-key usage in the selected month.
          </li>
          <li className="flex gap-2">
            <ChevronRight size={14} className="mt-1 shrink-0 text-indigo-400" />
            <span>
              Quota can be <span className="font-medium">0</span>. <span className="font-medium">Inactive</span> assignments
              are stored but do not apply.
            </span>
          </li>
        </ul>
      </section>

      <section className={cardClass(isLight)}>
        <h2 className={`text-sm font-medium ${title}`}>How overrides work</h2>
        <p className={`mt-1 text-xs ${muted}`}>The first active match wins. Inactive or missing grants are skipped.</p>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-0">
          {overrideSteps.map((step, index) => {
            const Icon = step.icon;
            return (
              <React.Fragment key={step.label}>
                {index > 0 ? <SkipLabel isLight={isLight} /> : null}
                <div className={`flex-1 min-w-0 rounded-xl px-3 py-3 ${chip}`}>
                  <span className={iconWrap(isLight, step.tone)}>
                    <Icon size={15} />
                  </span>
                  <p className={`mt-2.5 text-sm font-medium ${title}`}>{step.label}</p>
                  <p className={`text-[11px] mt-0.5 leading-relaxed ${muted}`}>{step.hint}</p>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <ul className={`mt-4 space-y-2 text-sm leading-relaxed ${body}`}>
          <li className="flex gap-2">
            <ChevronRight size={14} className="mt-1 shrink-0 text-indigo-400" />
            Check in this order: <span className="font-medium">user → role → department → 0</span>.
          </li>
          <li className="flex gap-2">
            <ChevronRight size={14} className="mt-1 shrink-0 text-indigo-400" />
            <span>
              The first <span className="font-medium">active</span> match wins. A user assignment replaces a role or
              department grant for that person only.
            </span>
          </li>
          <li className="flex gap-2">
            <Ban size={14} className={`mt-1 shrink-0 ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
            Inactive or missing assignments are skipped so the next level applies.
          </li>
        </ul>
      </section>
    </div>
  );
}
